import { z } from "zod";
const ulidSchema = z
    .string()
    .length(26)
    .regex(/^[0123456789ABCDEFGHJKMNPQRSTVWXYZ]+$/);
const safeIdentifierSchema = z
    .string()
    .min(1)
    .max(128)
    .regex(/^[a-z0-9][a-z0-9_.:-]*$/);
const hashSchema = z
    .string()
    .length(64)
    .regex(/^[0-9a-f]+$/);
const safeTableNameSchema = z
    .string()
    .min(1)
    .max(80)
    .regex(/^[a-z][a-z0-9_]*$/);
export const accountDeletionExecutorStepSchema = z.enum([
    "preflight_blockers",
    "resolve_deleted_subject",
    "revoke_web_sessions",
    "revoke_oauth_grants",
    "purge_private_caches",
    "delete_auth_credentials",
    "transform_commercial",
    "transform_support",
    "transform_moderation_audit",
    "delete_learning_state",
    "write_tombstone",
    "delete_owner_user",
    "complete_request",
]);
export const accountDeletionExecutorSafeCodeSchema = z.enum([
    "active_legal_hold",
    "unresolved_refund",
    "unresolved_dispute",
    "value_disposition_required",
    "active_admin_role",
    "last_superadmin",
    "ownership_transfer_required",
    "guardian_separate_learner_delete_not_allowed",
    "tombstone_key_unavailable",
    "support_ops_launch_gate",
    "unresolved_checkout_or_payment",
    "unsafe_late_callback_window",
    "open_support_request",
    "oauth_grant_helper_unavailable",
    "oauth_grant_revocation_incomplete",
    "cache_purge_incomplete",
    "unsupported_live_reference",
    "restrictive_reference_remaining",
    "checkpoint_rejected",
    "lease_lost",
    "executor_step_failed",
]);
export const accountDeletionExecutorTableCountSchema = z.strictObject({
    table: safeTableNameSchema,
    affectedRows: z.number().int().min(0).max(1_000_000),
});
export const accountDeletionRetainedDataRationaleSchema = z.strictObject({
    category: safeIdentifierSchema,
    action: z.enum(["deleted", "anonymized", "surrogate_replaced", "retained_no_identity"]),
    reasonCode: safeIdentifierSchema,
    tables: z.array(safeTableNameSchema).min(1).max(40),
});
export const accountDeletionExecutorCheckpointSchema = z
    .strictObject({
    schema: z.literal("tutorlite.account-deletion-executor-checkpoint/v1"),
    completedSteps: z.array(accountDeletionExecutorStepSchema).max(32).default([]),
    lastStep: accountDeletionExecutorStepSchema.optional(),
    deletedSubjectId: ulidSchema.optional(),
    tombstone: z
        .strictObject({
        hashKeyVersion: safeIdentifierSchema,
        canonicalizationVersion: safeIdentifierSchema,
        written: z.boolean(),
    })
        .optional(),
    retainedDataRationale: z.array(accountDeletionRetainedDataRationaleSchema).max(40).default([]),
    requestMetadata: z
        .strictObject({
        valueDisposition: z.enum(["none", "refund_review", "forfeiture_selection"]),
        requestPreviewHash: hashSchema,
        requestIdempotencyHash: hashSchema,
        cancelIdempotencyHash: hashSchema.optional(),
    })
        .optional(),
    tableCounts: z.array(accountDeletionExecutorTableCountSchema).max(80).default([]),
    retry: z
        .strictObject({
        code: accountDeletionExecutorSafeCodeSchema,
        step: accountDeletionExecutorStepSchema.optional(),
        at: z.number().int().min(0).max(9_007_199_254_740_991),
    })
        .optional(),
})
    .superRefine((checkpoint, context) => {
    const serialized = JSON.stringify(checkpoint);
    if (serialized.length > 65_536) {
        context.addIssue({ code: "custom", message: "Checkpoint exceeds 65536 bytes" });
    }
});
export const accountDeletionExecutorSafeErrorSchema = z.strictObject({
    code: accountDeletionExecutorSafeCodeSchema,
    step: accountDeletionExecutorStepSchema.optional(),
    retryable: z.boolean(),
});
export const accountDeletionExecutorResultSchema = z.discriminatedUnion("status", [
    z.strictObject({
        status: z.literal("completed"),
        requestId: ulidSchema,
        deletedSubjectId: ulidSchema,
    }),
    z.strictObject({
        status: z.literal("blocked"),
        requestId: ulidSchema,
        error: accountDeletionExecutorSafeErrorSchema,
    }),
    z.strictObject({
        status: z.literal("retryable_error"),
        requestId: ulidSchema,
        error: accountDeletionExecutorSafeErrorSchema,
    }),
    z.strictObject({ status: z.literal("lease_lost"), requestId: ulidSchema }),
    z.strictObject({ status: z.literal("no_claim") }),
]);
export function emptyAccountDeletionExecutorCheckpoint() {
    return accountDeletionExecutorCheckpointSchema.parse({
        schema: "tutorlite.account-deletion-executor-checkpoint/v1",
    });
}
//# sourceMappingURL=account-deletion-executor.js.map